import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Dashboard, { DashboardSizeSettings } from './Dashboard';
import DashboardPanelEditor, { createPanel } from './DashboardPanelEditor';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

const bottomRightY = rect => rect.y + rect.h;

// Find a suitable x,y coordinate to place a new panel
export const findPanelInsertionPos = (panels, width, dashboardSizeSettings) => {
  const gridPositions = _.map(panels, 'grid');
  const maxY = _.maxBy(gridPositions, 'y')?.y ?? 0;
  const gridPositionsOnRow = _.filter(gridPositions, grid => grid.y === maxY);
  const maxXPanelOnRow = _.maxBy(gridPositionsOnRow, 'x');
  const maxXOnRow = (maxXPanelOnRow == null) ? 0 : (maxXPanelOnRow.x + maxXPanelOnRow.w);

  if ((maxXOnRow + width) <= dashboardSizeSettings.columnCount) {
    return { x: maxXOnRow, y: maxY };
  }

  const maxYNewLineGridPos = _.maxBy(gridPositions, bottomRightY) ?? { y: 0, h: 0 };
  return { x: 0, y: maxYNewLineGridPos.y + maxYNewLineGridPos.h };
};

const createPanelNewGrid = (panels, dashboardSizeSettings) => {
  return {
    w: dashboardSizeSettings.panelMinWidth,
    h: dashboardSizeSettings.panelMinHeight,
    ...findPanelInsertionPos(panels, dashboardSizeSettings.panelMinWidth, dashboardSizeSettings)
  };
};

const DashboardEditor = ({ dashboard, onDashboardUpdated, children }) => {

  const [selectedPanel, setSelectedPanel] = useState(null);

  const onUpdatePanel = useCallback(newPanel => {
    if (newPanel == null) {
      setSelectedPanel(null);
    } else if (newPanel.id) {
      const newDashboard = _.cloneDeep(dashboard);

      const panelItemPosition = _.findIndex(newDashboard.data.panels, { id: newPanel.id });
      newDashboard.data.panels[panelItemPosition] = { ...newPanel };
      onDashboardUpdated(newDashboard);
      setSelectedPanel(newPanel);
    } else {
      newPanel.id = uuidv4();
      newPanel.grid = createPanelNewGrid(dashboard.data?.panels, DashboardSizeSettings);
      const newDashboard = { ...dashboard };
      newDashboard.data = newDashboard.data ?? {};
      newDashboard.data.panels = [...(dashboard.data?.panels ?? []), newPanel];
      onDashboardUpdated(newDashboard);
    }

  }, [dashboard, onDashboardUpdated]);

  const onLayoutChanged = useCallback(newLayout => {
    let panelWasMoved = false;
    const newPanels = _.map(dashboard.data?.panels, panel => {
      const grid = _.find(newLayout, { i: panel.id });
      if (!_.isEqual(grid, panel.grid)) {
        panelWasMoved = true;
      }
      return { ...panel, grid: { ...grid } };
    });

    // Only update if the grid position have changed
    if (panelWasMoved) {
      return onDashboardUpdated({ ...dashboard, data: { ...(dashboard.data ?? {}), panels: newPanels } });
    }
  }, [dashboard, onDashboardUpdated]);

  const onDeletePanel = useCallback(panel => {
    const oldCurrentDashboard = _.cloneDeep(dashboard);
    oldCurrentDashboard.data.panels = _.reject([...oldCurrentDashboard.data?.panels], { id: panel.id });
    setSelectedPanel(oldSelectedPanel => oldSelectedPanel.id === panel.id ? null : oldSelectedPanel);
    onDashboardUpdated(oldCurrentDashboard);
  }, [dashboard, onDashboardUpdated]);

  const onEditPanel = useCallback(panel => {
    setSelectedPanel(panel);
  }, []);

  const createPanelCallback = useCallback((panelTypeIndex) => {
    const newPanel = createPanel(panelTypeIndex);
    onUpdatePanel(newPanel);
  }, [onUpdatePanel]);

  if (_.isFunction(children)) {
    children({ onEditPanel, onDeletePanel, createPanel: createPanelCallback });
  }

  return (
    <>
      <DashboardPanelEditor
        panel={selectedPanel}
        onPanelChanged={onUpdatePanel}
        onDeletePanel={onDeletePanel}
      />

      <Dashboard
        dashboard={dashboard}
        isResizable
        isDraggable
        selectedPanelId={selectedPanel?.id}
        onEditPanel={onEditPanel}
        onLayoutChange={onLayoutChanged}
        {...DashboardSizeSettings}
      />
    </>
  );
};

DashboardEditor.propTypes = {
  dashboard: PropTypes.object,
  onDashboardUpdated: PropTypes.func.isRequired,
  children: PropTypes.func
};
export default React.memo(DashboardEditor);
